/**
 * Authentication context provider for managing AI provider auth state
 * Supports multiple simultaneous provider connections with active provider switching
 */

import React, { createContext, useEffect, useState } from "react";
import type { AIClient } from "@/lib/ai/types.ts";
import type { AIProvider, AuthTokens } from "@/lib/auth/tokens.ts";
import {
  areTokensExpired,
  clearTokens,
  clearTokensForProvider,
  getConnectedProviders,
  loadAllTokens,
  loadTokens,
  saveTokens,
  setActiveProvider as setActiveProviderStorage,
} from "@/lib/auth/tokens.ts";
import { CopilotClient } from "@/lib/ai/copilot.ts";
import { AntigravityClient } from "@/lib/ai/antigravity.ts";
import { OpenRouterClient } from "@/lib/ai/openrouter.ts";
import { GeminiClient } from "@/lib/ai/gemini.ts";
import { GroqClient } from "@/lib/ai/groq.ts";
import { PerplexityClient } from "@/lib/ai/perplexity.ts";
import { DefaultClient } from "@/lib/ai/default.ts";

// ============================================================================
// Context Types
// ============================================================================

export interface AuthState {
  isAuthenticated: boolean;
  isExpired: boolean;
  provider: AIProvider | null;
  tokens: AuthTokens | null;
  client: AIClient | null;
  connectedProviders: AIProvider[];
}

export interface AuthContextValue extends AuthState {
  // Authentication actions
  login: (tokens: AuthTokens) => void;
  logout: () => void;
  logoutProvider: (provider: AIProvider) => void;
  refreshAuthState: () => void;

  // Provider switching
  setActiveProvider: (provider: AIProvider) => void;

  // Client factory
  getClient: () => AIClient | null;
}

// ============================================================================
// Context
// ============================================================================

export const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>(() => {
    const tokens = loadTokens();
    const connectedProviders = getConnectedProviders();
    return {
      isAuthenticated: tokens !== null,
      isExpired: tokens ? areTokensExpired(tokens) : false,
      provider: tokens?.provider ?? null,
      tokens,
      client: tokens ? createClient(tokens) : null,
      connectedProviders,
    };
  });

  /**
   * Ensure the default provider is always connected.
   * Called on mount so users have a working provider immediately.
   */
  const ensureDefaultProvider = () => {
    const allTokens = loadAllTokens();
    if (!allTokens["default"]) {
      saveTokens({
        provider: "default",
        accessToken: "",
      });
    }
  };

  /**
   * Load tokens from localStorage on mount and ensure default provider is connected
   */
  useEffect(() => {
    ensureDefaultProvider();
    refreshAuthState();
  }, []);

  /**
   * Login with new tokens (adds to multi-provider storage)
   */
  const login = (tokens: AuthTokens) => {
    saveTokens(tokens);
    refreshAuthState();
  };

  /**
   * Logout all providers
   */
  const logout = () => {
    clearTokens();
    setState({
      isAuthenticated: false,
      isExpired: false,
      provider: null,
      tokens: null,
      client: null,
      connectedProviders: [],
    });
  };

  /**
   * Logout a specific provider
   */
  const logoutProvider = (provider: AIProvider) => {
    clearTokensForProvider(provider);
    refreshAuthState();
  };

  /**
   * Switch to a different connected provider
   */
  const setActiveProvider = (provider: AIProvider) => {
    setActiveProviderStorage(provider);
    refreshAuthState();
  };

  /**
   * Refresh auth state from localStorage
   */
  const refreshAuthState = () => {
    const tokens = loadTokens();
    const connectedProviders = getConnectedProviders();

    if (!tokens) {
      setState({
        isAuthenticated: false,
        isExpired: false,
        provider: null,
        tokens: null,
        client: null,
        connectedProviders,
      });
      return;
    }

    const expired = areTokensExpired(tokens);
    setState({
      isAuthenticated: true,
      isExpired: expired,
      provider: tokens.provider,
      tokens,
      client: expired ? null : createClient(tokens),
      connectedProviders,
    });
  };

  /**
   * Get current AI client
   */
  const getClient = (): AIClient | null => {
    if (state.isExpired || !state.tokens) return null;
    return state.client;
  };

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    logoutProvider,
    refreshAuthState,
    setActiveProvider,
    getClient,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create an AI client instance based on provider type
 */
function createClient(tokens: AuthTokens): AIClient | null {
  try {
    switch (tokens.provider) {
      case "default":
        return new DefaultClient();
      case "copilot":
        return new CopilotClient(tokens.accessToken);
      case "antigravity":
        return new AntigravityClient(tokens.accessToken);
      case "openrouter":
        return new OpenRouterClient(tokens.accessToken);
      case "gemini":
        return new GeminiClient(tokens.accessToken);
      case "groq":
        return new GroqClient(tokens.accessToken);
      case "perplexity":
        return new PerplexityClient(tokens.accessToken);
      default:
        console.error(`Unknown provider: ${tokens.provider}`);
        return null;
    }
  } catch (error) {
    console.error("Failed to create AI client:", error);
    return null;
  }
}
